/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { usageCreateStripePortalSession } from "../funcs/usageCreateStripePortalSession.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateStripePortalSessionRequest,
  CreateStripePortalSessionSecurity,
} from "../models/operations/createstripeportalsession.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateStripePortalSessionMutationVariables = {
  request?: CreateStripePortalSessionRequest | undefined;
  security?: CreateStripePortalSessionSecurity | undefined;
  options?: RequestOptions;
};

export type CreateStripePortalSessionMutationData = string;

export type CreateStripePortalSessionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * createStripePortalSession usage
 *
 * @remarks
 * Create a Stripe customer portal session for the organization's PAYG subscription
 */
export function useCreateStripePortalSessionMutation(
  options?: MutationHookOptions<
    CreateStripePortalSessionMutationData,
    CreateStripePortalSessionMutationError,
    CreateStripePortalSessionMutationVariables
  >,
): UseMutationResult<
  CreateStripePortalSessionMutationData,
  CreateStripePortalSessionMutationError,
  CreateStripePortalSessionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateStripePortalSessionMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateStripePortalSession(): MutationKey {
  return ["@gram/client", "usage", "createStripePortalSession"];
}

export function buildCreateStripePortalSessionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateStripePortalSessionMutationVariables,
  ) => Promise<CreateStripePortalSessionMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateStripePortalSession(),
    mutationFn: function createStripePortalSessionMutationFn({
      request,
      security,
      options,
    }): Promise<CreateStripePortalSessionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(usageCreateStripePortalSession(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
